import React from 'react';
import { useDataProvider } from 'react-admin';
import { UserMinus } from 'lucide-react';
import { ConfirmDialog } from '../ui/confirm-dialog';
import { toastSuccess, toastError } from '../../lib/toast';
import { CustomDataProvider } from '../../types/dataProvider';

/**
 * Removal applies only to a live group-class booking (pending or confirmed).
 * Shared by the session roster and the booking detail so the gate can't drift.
 */
export function canRemoveFromClass(sessionType?: string, bookingStatus?: string): boolean {
  return sessionType === 'group_class' && (bookingStatus === 'pending' || bookingStatus === 'confirmed');
}

/**
 * FEAT-030 — support removes a player from a group class.
 *
 * POST /admin/bookings/{id}/remove: no time window, audited server-side, and
 * the player can't rejoin that class. The optional reason is private to
 * admins. A booking with an approved/in-flight payment is refused (409) until
 * it is refunded from Payments; the backend's sentence is shown as-is.
 */
export function RemoveFromClassAction({
  bookingId,
  playerName,
  sessionId,
  variant,
  onRemoved,
}: {
  bookingId: number;
  playerName?: string;
  sessionId?: number;
  variant: 'icon' | 'button';
  onRemoved: () => void;
}) {
  const dataProvider = useDataProvider<CustomDataProvider>();
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const who = playerName || `booking #${bookingId}`;

  const remove = async (reason?: string) => {
    setBusy(true);
    try {
      await dataProvider.removeBooking({ id: bookingId, reason: reason || undefined });
      toastSuccess(`${playerName || 'Player'} removed from the class`);
      onRemoved();
    } catch (e: any) {
      toastError(e?.message || 'Failed to remove participant');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {variant === 'icon' ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label="Remove from class"
          title="Remove from class"
          className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error"
        >
          <UserMinus className="size-4" />
        </button>
      ) : (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="inline-flex h-9 items-center gap-2 rounded-pill border border-error/40 px-4 text-sm font-semibold text-error hover:bg-error-bg"
        >
          <UserMinus className="size-4" />
          Remove from class
        </button>
      )}
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="destructive"
        title={sessionId ? `Remove ${who} from class #${sessionId}?` : `Remove ${who} from this class?`}
        consequence="They're notified, lose chat access, and can't rejoin this class."
        confirmLabel="Remove"
        cancelLabel="Keep"
        withReason
        reasonLabel="Reason (optional, private)"
        reasonPlaceholder="Optional — never shown to the player"
        reasonMaxLength={500}
        isLoading={busy}
        onConfirm={remove}
      />
    </>
  );
}
